import {
	CheckCircleOutline,
	HighlightOff,
	InfoOutlined,
	Launch
} from '@mui/icons-material';
import {
	Alert,
	Box,
	Button,
	Stack,
	Tooltip,
	Typography,
	useTheme
} from '@mui/material';
import { get, post } from 'api/api';
import { API_ROUTES } from 'api/apiRoutes';
import { ISSOTestStartBody, useStartTestSSO } from 'api/configurations';
import { useCurrentIdP } from 'api/idps';
import QueryKeys from 'api/queryKeys';
import ValidationErrorsAlert from 'app/ValidationErrorsAlert';
import LoadingButton from 'components/shared/LoadingButton';
import { ROUTES } from 'constants/routes';
import { Method } from 'consts';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import {
	useDirtyConfigs,
	useFieldsDirty,
	useSaveSSOConfigFields
} from 'contexts/SSOConfigFieldsContext';
import { useEffectOnceWhen, useQueryParams, useToast } from 'hooks';
import PageError from 'illustrations/PageError';
import { isEmpty, mapValues } from 'lodash';
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react';
import { UNSAFE_DataRouterContext } from 'react-router-dom';
import { IDPResponse } from 'types/api';
import { ToastSeverity } from 'types/toast';
import { getSSODomains } from 'utils/configUtils';
import { isBroadcastChannelSupported, isInIframe } from 'utils/isInIframe';
import { useNav } from 'utils/navigation';
import FeatureFlagCheck from 'components/FeatureFlagCheck';
import { CONTENT_PADDING_BOTTOM } from 'components/PageLayout/PageLayout';
import { useDialogsContext } from 'contexts/Dialogs/DialogsContext';
import { FeatureFlagId } from 'types/featureflags';
import TestResults from './TestResults';
import useSSOTestCallbackListener from './useSSOTestCallbackListener';
import UnsavedConfigBlocker from './UnsavedConfigBlocker';

const SSO_TEST_ERROR_ID_KEY = 'ssoTestErrorId';

const TestingPage: React.FC = () => {
	const { data: idpData } = useCurrentIdP();
	const { method } = useIdPConfigContext();

	const {
		palette: {
			mainPaperBackground,
			grey: { '200': grey200, '600': grey600 }
		},
		spacing
	} = useTheme();
	// Sticky offsets are measured from the scroll container's padding edge, so
	// cancel the layout's bottom padding to pin the bar to the card's edge.
	const stickyBarBottom = mapValues(CONTENT_PADDING_BOTTOM, (pb) =>
		spacing(-pb)
	);
	const nav = useNav();
	const { addToast } = useToast();

	const { isDirty, discard } = useFieldsDirty();
	const { validationErrors, fieldsUniqueErrorMessages, save } =
		useSaveSSOConfigFields();
	const { openSaveUntestedDialog, openSaveConfirmDialog } = useDialogsContext();
	const dirtyConfigs = useDirtyConfigs();

	const [isTesting, setIsTesting] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const isBusy = isTesting || isSaving;
	const [code, setCode] = useQueryParams(QueryKeys.code);
	const [ssoCallback] = useQueryParams(QueryKeys.ssoCallback);
	const [result, setResult] = useState<IDPResponse | null>(null);
	const [queryError, setQueryError] = useQueryParams(QueryKeys.err);
	const [error, setError] = useState<string>(queryError); // preserve err query param after cleanup
	const [errorResult, setErrorResult] = useState<IDPResponse | null>(null);

	const startTestSSOMutation = useStartTestSSO();

	const shouldBlock = !!result && isDirty;
	const hasDataRouter = !!useContext(UNSAFE_DataRouterContext);

	useEffect(() => {
		if (!shouldBlock) return undefined;
		/* istanbul ignore next */
		const handler = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [shouldBlock]);

	useSSOTestCallbackListener({
		onError: setError,
		onSuccess: setResult,
		onSettled: () => setIsTesting(false)
	});

	const test = useCallback(async () => {
		setIsTesting(true);
		setError('');

		const body: ISSOTestStartBody = { redirectUrl: window.location.href };

		if (isDirty && dirtyConfigs) {
			if (method === Method.Saml) {
				body.saml = { ...dirtyConfigs.saml, enabled: true };
			}
			if (method === Method.Oidc) {
				body.oauth = { ...dirtyConfigs.oauth, enabled: true };
			}
			// Draft SSO domains are only persisted on the tenant when the config is
			// saved, so pass them along for the test's email-domain-mismatch check.
			body.domains = getSSODomains(dirtyConfigs, method ?? undefined) || [];
		}

		try {
			if (isInIframe() && isBroadcastChannelSupported()) {
				const redirectUrl = new URL(window.location.href);
				redirectUrl.searchParams.set(QueryKeys.ssoCallback, 'true');
				redirectUrl.searchParams.delete(QueryKeys.code);
				redirectUrl.searchParams.delete(QueryKeys.err);
				body.redirectUrl = redirectUrl.toString();

				const { url: idpUrl, errorId } =
					await startTestSSOMutation.mutateAsync(body);
				sessionStorage.setItem(SSO_TEST_ERROR_ID_KEY, errorId ?? '');
				const popup = window.open(idpUrl, '_blank');
				if (!popup) {
					addToast({
						severity: ToastSeverity.Error,
						content: intl.tr('ssoConfig.testPage.popup.blocked')
					});
					setIsTesting(false);
					return () => null;
				}

				const timeout = setTimeout(() => {
					/* istanbul ignore next  */
					setIsTesting(false);
				}, 5000);

				return () => clearTimeout(timeout);
			}

			const { url: idpUrl, errorId } =
				await startTestSSOMutation.mutateAsync(body);
			sessionStorage.setItem(SSO_TEST_ERROR_ID_KEY, errorId ?? '');
			window.location.replace(idpUrl);
		} catch (e) {
			const errorCode = (e as any).response?.data?.errorCode || '';
			const errorMessage = (e as any).response?.data?.errorMessage || '';
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('ssoConfig.testPage.redirect.failed', {
					error: `${errorCode ? `[${errorCode}]` : ''}: ${errorMessage}`
				})
			});
			setIsTesting(false);
		}
		return () => null;
	}, [startTestSSOMutation, addToast, isDirty, dirtyConfigs, method]);

	const handleSave = useCallback(async () => {
		if (!save) return;
		setIsSaving(true);
		try {
			await save();
		} finally {
			setIsSaving(false);
		}
	}, [save]);

	const handleSaveUntested = useCallback(async () => {
		const { proceed } = await openSaveUntestedDialog();
		if (!proceed) return;
		await handleSave();
	}, [openSaveUntestedDialog, handleSave]);

	const handleSaveTested = useCallback(async () => {
		const { proceed } = await openSaveConfirmDialog();
		if (!proceed) return;
		await handleSave();
	}, [openSaveConfirmDialog, handleSave]);

	useEffectOnceWhen(
		!!idpData?.id && !!code && (!error || !queryError) && !ssoCallback,
		() => {
			(async () => {
				try {
					const res = await post<{ token: string }, IDPResponse>(
						API_ROUTES.ssoTest.end,
						{ token: code! }
					);
					setResult(res.data);
				} catch {
					addToast({
						severity: ToastSeverity.Error,
						content: intl.tr('ssoConfig.testPage.failed')
					});
				} finally {
					setCode(null);
				}
			})();
		},
		[code, setCode]
	);

	// in case of error we want to clear all query params
	useEffectOnceWhen(
		!!idpData?.id && !!queryError,
		() => {
			(async () => {
				setCode(null);
				setQueryError(null);
			})();
		},
		[queryError, setCode, setQueryError]
	);

	const fetchedErrorIdRef = useRef<string | null>(null);
	useEffect(() => {
		if (!error) {
			setErrorResult(null);
			fetchedErrorIdRef.current = null;
			return;
		}
		const errorId = sessionStorage.getItem(SSO_TEST_ERROR_ID_KEY);
		if (!errorId || fetchedErrorIdRef.current === errorId) return;
		fetchedErrorIdRef.current = errorId;
		(async () => {
			try {
				const res = await get<IDPResponse>(
					`${API_ROUTES.ssoTest.error}?errorId=${encodeURIComponent(errorId)}`
				);
				if (res.data?.samlResponse || res.data?.oidcResponse) {
					setErrorResult(res.data);
				}
			} catch {
				setErrorResult(null);
			} finally {
				sessionStorage.removeItem(SSO_TEST_ERROR_ID_KEY);
			}
		})();
	}, [error]);

	const handleDone = useCallback(() => {
		nav(ROUTES.home);
	}, [nav]);

	const handleTestAgain = useCallback(() => {
		setResult(null);
		setQueryError(null);
		test();
	}, [setQueryError, test]);

	const validationTooltipProps = {
		title: fieldsUniqueErrorMessages?.join('\n') || '',
		slotProps: {
			popper: {
				sx: {
					whiteSpace: 'pre',
					textWrap: 'wrap'
				}
			}
		}
	};

	return (
		<Stack gap={3} height="100%">
			{hasDataRouter && <UnsavedConfigBlocker shouldBlock={shouldBlock} />}
			{!error && !result && (
				<>
					<Typography variant="h2">
						{intl.tr('ssoConfig.testPage.pageTitle', {
							idpName: idpData?.name,
							method: intl.tr('general.methodUpper', { method })
						})}
					</Typography>
					<Typography>{intl.tr('ssoConfig.testPage.pageMessage')}</Typography>
				</>
			)}

			{error && errorResult && (
				<Stack flexGrow={1} gap={3}>
					<Stack gap={2}>
						<Stack direction="row" alignItems="center" gap={1}>
							<Typography variant="h3">
								{intl.tr('ssoConfig.testPage.failed.title')}
							</Typography>
							<HighlightOff color="error" />
						</Stack>
						<Alert variant="outlined" severity="error">
							{intl.tr('ssoConfig.testPage.error', { error })}
						</Alert>
					</Stack>

					<TestResults result={errorResult} />
					<Stack
						borderTop={`1px solid ${grey200}`}
						direction="row"
						gap={3}
						py={2}
						justifyContent="flex-end"
						sx={{
							position: 'sticky',
							bottom: stickyBarBottom,
							bgcolor: mainPaperBackground
						}}
					>
						<LoadingButton
							data-testid="test-again-button"
							variant="contained"
							onClick={handleTestAgain}
							isLoading={isTesting}
						>
							{intl.tr('ssoConfig.testPage.testAgainButton')}
						</LoadingButton>
					</Stack>
				</Stack>
			)}

			{error && !errorResult && (
				<Stack alignItems="center" justifyContent="center" flexGrow={1} gap={2}>
					<PageError data-testid="sso-error-image" />
					<Alert variant="outlined" severity="error">
						{intl.tr('ssoConfig.testPage.error', { error })}
					</Alert>
					<LoadingButton
						data-testid="test-again-button"
						variant="contained"
						onClick={handleTestAgain}
						isLoading={isTesting}
					>
						{intl.tr('ssoConfig.testPage.testAgainButton')}
					</LoadingButton>
				</Stack>
			)}

			{result && (
				<Stack flexGrow={1} gap={3}>
					<Stack gap={2}>
						<Stack direction="row" alignItems="center" gap={1}>
							<Typography variant="h3">
								{intl.tr('ssoConfig.testPage.completed.title')}
							</Typography>
							<CheckCircleOutline color="success" />
						</Stack>
						<Typography>
							{isDirty
								? intl.tr('ssoConfig.testPage.completed.messageUnsaved')
								: intl.tr('ssoConfig.testPage.completed.message')}
						</Typography>
					</Stack>

					<TestResults result={result} />
					<Stack
						borderTop={`1px solid ${grey200}`}
						direction="row"
						gap={3}
						py={2}
						justifyContent="space-between"
						sx={{
							position: 'sticky',
							bottom: stickyBarBottom,
							bgcolor: mainPaperBackground
						}}
					>
						{isDirty ? (
							<Stack
								role="alert"
								pl={1}
								direction="row"
								gap={1}
								alignItems="center"
								color={grey600}
							>
								<InfoOutlined />
								{intl.tr('ssoConfig.testPage.completed.unsavedWarning')}
							</Stack>
						) : (
							<Box />
						)}
						<Stack gap={3} direction="row" alignItems="center">
							<Button
								data-testid="test-again-button"
								onClick={handleTestAgain}
								variant="outlined"
								disabled={isBusy}
							>
								{intl.tr('ssoConfig.testPage.testAgainButton')}
							</Button>
							<Tooltip {...validationTooltipProps}>
								<Box>
									<LoadingButton
										data-testid="save-sso-button"
										disabled={
											!isDirty || !isEmpty(validationErrors) || isTesting
										}
										variant="contained"
										onClick={handleSaveTested}
										isLoading={isSaving}
									>
										{intl.tr('ssoConfig.testPage.saveButton')}
									</LoadingButton>
								</Box>
							</Tooltip>
							{!isDirty && (
								<LoadingButton
									data-testid="done-button"
									onClick={handleDone}
									variant="contained"
									disabled={isBusy}
								>
									{intl.tr('ssoConfig.testPage.doneButton')}
								</LoadingButton>
							)}
						</Stack>
					</Stack>
				</Stack>
			)}

			{!error && !result && (
				<Stack gap={3}>
					{!isEmpty(validationErrors) && (
						<ValidationErrorsAlert messages={fieldsUniqueErrorMessages || []} />
					)}
					<Stack direction="row" gap={3} justifyContent="end">
						{isDirty && (
							<Button
								data-testid="discard-changes-button"
								onClick={discard}
								variant="outlined"
								disabled={isBusy}
							>
								{intl.tr('ssoConfig.testPage.discardChangesButton')}
							</Button>
						)}

						<FeatureFlagCheck flag={FeatureFlagId.SHOW_SAVE_WHEN_UNTESTED}>
							<Tooltip {...validationTooltipProps}>
								<Box>
									<LoadingButton
										data-testid="save-sso-button"
										disabled={
											!isDirty || !isEmpty(validationErrors) || isTesting
										}
										variant="outlined"
										onClick={handleSaveUntested}
										isLoading={isSaving}
									>
										{intl.tr('ssoConfig.testPage.saveButton')}
									</LoadingButton>
								</Box>
							</Tooltip>
						</FeatureFlagCheck>

						<Tooltip {...validationTooltipProps}>
							<Box>
								<LoadingButton
									disabled={!isEmpty(validationErrors) || isSaving}
									variant="contained"
									onClick={test}
									data-testid="test-sso-button"
									isLoading={isTesting}
									startIcon={isInIframe() ? <Launch /> : undefined}
								>
									{intl.tr('ssoConfig.testPage.testButton')}
								</LoadingButton>
							</Box>
						</Tooltip>
					</Stack>
				</Stack>
			)}
		</Stack>
	);
};

export default TestingPage;
